import {
  Button,
  Alert,
  Combobox,
  PhoneInputField,
  FormField,
  TextInput,
  useToast,
} from "@a2z/ui";

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import { pickLocalized } from "@/lib/i18n/localized";
import { toE164OrNull } from "@/lib/phone/phone";
import { getInviteAbsoluteUrl } from "@/lib/routing/portal-urls";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useCreateSellerMutation } from "../services/useCreateSellerMutation";
import { getRequiredFieldErrors } from "@/shared/components/forms/validation";
import { getPhoneInputLabels } from "@/shared/components/forms/phone-input-labels";
import { ShareLinkButton } from "@/shared/components/ShareLinkButton";
import { handleAdminSellerCreateSuccess } from "./seller-create-success";

// The seller's first subscription starts on the plan's defaults (amount, period,
// start today); the audit reason is fixed since there's nothing being "changed"
// at creation time. Start date / reason / overrides are only editable later from
// the store's subscription screen.
const INITIAL_SUBSCRIPTION_REASON = "Initial subscription";

type InviteInfo = {
  sellerId?: string;
  inviteToken: string;
  expiresAt: string;
};

export function SellerCreateForm({
  locale,
  plans,
}: {
  locale: Locale;
  plans: { id: string; nameAr: string; nameEn: string | null }[];
}) {
  const navigate = useNavigate();
  const t = useTranslations();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInviteDetailsOpen, setIsInviteDetailsOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const { mutateAsync: createSeller } = useCreateSellerMutation();

  const inviteLink =
    invite?.inviteToken && typeof window !== "undefined"
      ? getInviteAbsoluteUrl(
          locale,
          invite.inviteToken,
          window.location.protocol === "http:" ? "http" : "https",
        )
      : "";

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setInvite(null);
    setIsSubmitting(true);
    const formData = new FormData(event.currentTarget);
    const requiredErrors = getRequiredFieldErrors(
      formData,
      ["name", "email", "storeName", "subscriptionPlanId"],
      t(keys.errors.required),
    );
    if (phone.trim().length === 0) {
      requiredErrors.phone = t(keys.errors.required);
    }

    if (Object.keys(requiredErrors).length > 0) {
      setFieldErrors(requiredErrors);
      setIsSubmitting(false);
      return;
    }
    const normalizedPhone = toE164OrNull(phone);
    if (!normalizedPhone) {
      setFieldErrors({ phone: t(keys.buyer.trackingValidationPhone) });
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = await createSeller({
        name: String(formData.get("name") ?? ""),
        email: String(formData.get("email") ?? ""),
        phone: normalizedPhone,
        storeName: String(formData.get("storeName") ?? ""),
        subscriptionPlanId: String(formData.get("subscriptionPlanId") ?? ""),
        subscriptionReason: INITIAL_SUBSCRIPTION_REASON,
      });
      const didNavigateToSellerDetails = handleAdminSellerCreateSuccess({
        locale,
        sellerId: payload.seller?.id,
        successMessage: t(keys.common.updateSuccess),
        toast,
        router: {
          replace: (href: string) => navigate(href, { replace: true }),
        },
      });
      if (didNavigateToSellerDetails) {
        return;
      }
      setInvite({
        sellerId: payload.seller?.id,
        inviteToken: payload.inviteToken,
        expiresAt: payload.expiresAt,
      });
      setIsInviteDetailsOpen(true);
      setIsSubmitting(false);
    } catch (error) {
      if (error instanceof ApiJsonClientError) {
        setError(error.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
    }
  };

  return (
    <form
      className="mx-auto flex w-full max-w-[640px] flex-col gap-4"
      onSubmit={handleSubmit}
    >
      {error ? (
        <Alert message={error} helperText={t(keys.errors.recovery)} />
      ) : null}
      {invite ? (
        <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--background-muted)] px-4 py-3 text-sm text-[color:var(--text-secondary)]">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="font-semibold text-[color:var(--text-primary)]">
              {t(keys.admin.inviteToken)}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                onClick={() => setIsInviteDetailsOpen((current) => !current)}
                variant="outline"
                className="h-8 rounded-full px-3 text-xs font-semibold transition max-lg:h-10"
                aria-expanded={isInviteDetailsOpen}
                aria-controls="seller-invite-details"
              >
                {isInviteDetailsOpen
                  ? t(keys.admin.inviteDetailsHide)
                  : t(keys.admin.inviteDetailsShow)}
              </Button>
              {inviteLink ? (
                <ShareLinkButton
                  path={inviteLink}
                  label={t(keys.common.copyInviteLink)}
                  copiedLabel={t(keys.common.copied)}
                  ariaLabel={t(keys.common.copyInviteLink)}
                  size="sm"
                  className="h-8"
                  toastMessage={t(keys.admin.inviteLinkCopiedToast)}
                />
              ) : null}
            </div>
          </div>
          {isInviteDetailsOpen ? (
            <div id="seller-invite-details" className="mt-3 min-w-0 space-y-2">
              <div className="text-xs text-[color:var(--text-muted)]">
                <span className="font-semibold text-[color:var(--text-secondary)]">
                  {t(keys.admin.inviteToken)}:
                </span>{" "}
                <span className="break-all">{invite.inviteToken}</span>
              </div>
              {inviteLink ? (
                <div className="min-w-0 text-xs text-[color:var(--text-muted)]">
                  <span className="font-semibold text-[color:var(--text-secondary)]">
                    {t(keys.common.copyInviteLink)}:
                  </span>{" "}
                  <span className="break-all">{inviteLink}</span>
                </div>
              ) : null}
              <div className="text-xs text-[color:var(--text-muted)]">
                {t(keys.admin.inviteExpires)}: {invite.expiresAt}
              </div>
              {invite.sellerId ? (
                <div className="pt-1">
                  <Link
                    to={`/${locale}/admin/sellers/${invite.sellerId}`}
                    className="text-xs font-semibold text-[color:var(--primary-600)]"
                  >
                    {t(keys.common.view)}
                  </Link>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="mt-2 text-xs text-[color:var(--text-muted)]">
              {t(keys.admin.inviteDetailsCollapsedHint)}
            </div>
          )}
        </div>
      ) : null}
      <FormField
        label={t(keys.common.name)}
        name="name"
        required
        error={fieldErrors.name}
      >
        <TextInput
          name="name"
          placeholder={t(keys.placeholders.name)}
          required
          hasError={Boolean(fieldErrors.name)}
        />
      </FormField>
      <div className="grid gap-4 lg:grid-cols-2">
        <FormField
          label={t(keys.common.email)}
          name="email"
          required
          error={fieldErrors.email}
        >
          <TextInput
            name="email"
            type="email"
            placeholder={t(keys.placeholders.email)}
            required
            hasError={Boolean(fieldErrors.email)}
          />
        </FormField>
        <FormField
          label={t(keys.common.phone)}
          name="phone"
          required
          error={fieldErrors.phone}
        >
          <PhoneInputField
            name="phone"
            value={phone}
            onChange={setPhone}
            locale={locale}
            labels={getPhoneInputLabels(t)}
            required
            hasError={Boolean(fieldErrors.phone)}
          />
        </FormField>
      </div>
      <FormField
        label={t(keys.common.storeName)}
        name="storeName"
        required
        error={fieldErrors.storeName}
      >
        <TextInput
          name="storeName"
          placeholder={t(keys.placeholders.storeName)}
          required
          hasError={Boolean(fieldErrors.storeName)}
        />
      </FormField>
      <FormField
        label={t(keys.admin.subscriptionSelectLabel)}
        name="subscriptionPlanId"
        required
        error={fieldErrors.subscriptionPlanId}
      >
        <input
          type="hidden"
          name="subscriptionPlanId"
          value={subscriptionPlanId}
        />
        <Combobox
          options={[
            { value: "", label: t(keys.admin.subscriptionSelectPlaceholder) },
            ...plans.map((plan) => ({
              value: plan.id,
              label: pickLocalized(locale, plan.nameAr, plan.nameEn),
            })),
          ]}
          value={subscriptionPlanId}
          onChange={(value) => setSubscriptionPlanId(value ?? "")}
        />
      </FormField>
      <Button
        type="submit"
        disabled={isSubmitting}
        variant="primary"
        className="h-11 w-full rounded-lg px-4 text-sm font-semibold transition disabled:opacity-60"
      >
        {t(keys.common.create)}
      </Button>
    </form>
  );
}
